import {supabase} from '../supabase/supabase';
import {uploadToR2} from './storageService';

const DELIVERY_RPC='mark_delivery_order_delivered_1791003000000';
const BUYER_CONFIRMATION_RPC='buyer_confirm_delivery_order_1791005000000';
const DISPUTE_TABLE='order_disputes_1793019000000';
const DISPUTE_MESSAGES_TABLE='order_dispute_messages_1793020000000';
const DISPUTE_EVIDENCE_TABLE='order_dispute_evidence_1793021000000';
const DISPUTE_OFFERS_TABLE='order_dispute_resolution_offers_1793022000000';

export const BUYER_DISPUTE_REASONS=[
  ['item_not_received','Item not received'],
  ['missing_items','Missing items'],
  ['wrong_items','Wrong items'],
  ['damaged_items','Damaged items'],
  ['quality_issue','Quality issue'],
  ['delivery_issue','Delivery issue'],
  ['pickup_issue','Pickup issue'],
  ['payment_issue','Payment issue'],
  ['other','Other']
];

export const SELLER_DISPUTE_REASONS=[
  ['missing_items','Customer says items are missing'],
  ['wrong_items','Customer received different items'],
  ['delivery_issue','Delivery or handoff problem'],
  ['pickup_issue','Pickup verification problem'],
  ['payment_issue','Payment or payout problem'],
  ['quality_issue','Quality complaint'],
  ['other','Other']
];

export async function markDeliveryOrderDelivered(orderId,metadata={}) {
  const {data,error}=await supabase.rpc(DELIVERY_RPC,{
    p_order_id:orderId,
    p_metadata:metadata
  });

  if (error) throw error;
  return data;
}

export async function buyerConfirmDeliveryOrder(orderId,metadata={}) {
  const {data,error}=await supabase.rpc(BUYER_CONFIRMATION_RPC,{
    p_order_id:orderId,
    p_metadata:metadata
  });

  if (error) throw error;
  return data;
}

export async function reportDeliveryIssue(
  orderId,
  description,
  files=[],
  reason='delivery_issue'
) {
  const {data:userData,error:userError}=await supabase.auth.getUser();

  if (userError) throw userError;

  const reporterId=userData.user?.id;

  if (!reporterId) {
    throw new Error('Authentication required.');
  }

  const {data,error}=await supabase
    .from(DISPUTE_TABLE)
    .insert({
      order_id:orderId,
      reporter_id:reporterId,
      reason,
      description:description.trim(),
      requested_resolution:'administrator_review'
    })
    .select()
    .single();

  if (error) throw error;

  if (files.length) {
    await uploadDisputeEvidence({
      disputeId:data.id,
      orderId,
      files
    });
  }

  return data;
}

export async function fetchOrderDispute(orderId) {
  const {data,error}=await supabase
    .from(DISPUTE_TABLE)
    .select('*')
    .eq('order_id',orderId)
    .order('created_at',{ascending:false})
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function fetchSellerDisputes(sellerId) {
  if (!sellerId) return [];

  const {data,error}=await supabase
    .from(DISPUTE_TABLE)
    .select(`
      *,
      order:orders!inner(
        id,
        seller_id,
        buyer_id,
        total_amount,
        payment_mode,
        payment_status,
        payment_captured,
        stripe_payment_intent_id,
        status,
        created_at
      )
    `)
    .eq('order.seller_id',sellerId)
    .order('created_at',{ascending:false});

  if (error) throw error;
  return data || [];
}

export async function fetchBuyerDisputes(userId) {
  if (!userId) return [];

  const {data,error}=await supabase
    .from(DISPUTE_TABLE)
    .select(`
      *,
      order:orders!inner(
        id,
        buyer_id,
        seller_id,
        total_amount,
        status,
        payment_status,
        created_at,
        seller:sellers(business_name)
      )
    `)
    .eq('order.buyer_id',userId)
    .order('created_at',{ascending:false});

  if (error) throw error;
  return data || [];
}

export async function fetchDisputeMessages(disputeId) {
  const {data,error}=await supabase
    .from(DISPUTE_MESSAGES_TABLE)
    .select('*,sender:users!order_dispute_messages_1793020000000_sender_id_fkey(id,name,role)')
    .eq('dispute_id',disputeId)
    .order('created_at',{ascending:true});

  if (error) throw error;
  return data || [];
}

export async function fetchDisputeOffers(disputeId) {
  const {data,error}=await supabase
    .from(DISPUTE_OFFERS_TABLE)
    .select('*')
    .eq('dispute_id',disputeId)
    .order('created_at',{ascending:true});

  if (error) throw error;
  return data || [];
}

export async function fetchDisputeEvidence(disputeId) {
  const {data,error}=await supabase
    .from(DISPUTE_EVIDENCE_TABLE)
    .select('*')
    .eq('dispute_id',disputeId)
    .order('created_at',{ascending:true});

  if (error) throw error;
  return data || [];
}

export async function uploadDisputeEvidence({
  disputeId,
  orderId,
  files=[]
}) {
  const {data:userData,error:userError}=await supabase.auth.getUser();

  if (userError) throw userError;

  const userId=userData.user?.id;

  if (!userId) {
    throw new Error('Authentication required.');
  }

  if (files.length>5) {
    throw new Error('You can upload up to 5 evidence images at a time.');
  }

  const evidence=[];

  for (const file of files) {
    if (!file.type.startsWith('image/')) {
      throw new Error('Evidence uploads must be image files.');
    }

    if (file.size>10*1024*1024) {
      throw new Error('Each evidence image must be 10 MB or smaller.');
    }

    const fileUrl=await uploadToR2(
      file,
      `users/${userId}/disputes/${disputeId}`
    );

    evidence.push({
      dispute_id:disputeId,
      order_id:orderId,
      uploaded_by:userId,
      file_name:file.name,
      file_url:fileUrl,
      mime_type:file.type,
      size_bytes:file.size
    });
  }

  if (!evidence.length) return [];

  const {data,error}=await supabase
    .from(DISPUTE_EVIDENCE_TABLE)
    .insert(evidence)
    .select();

  if (error) throw error;
  return data || [];
}

export async function sendSellerDisputeReply({
  disputeId,
  response,
  refundAmount=0
}) {
  const {data,error}=await supabase.functions.invoke(
    'stripe-order-dispute',
    {
      body:{
        action:'seller_offer',
        dispute_id:disputeId,
        response:response.trim(),
        refund_amount:refundAmount,
        resolution_type:refundAmount>0 ? 'partial_refund' : 'explanation'
      }
    }
  );

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function respondToDisputeOffer(disputeId,offerId,decision) {
  const {data,error}=await supabase.functions.invoke(
    'stripe-order-dispute',
    {
      body:{
        action:'buyer_offer_response',
        dispute_id:disputeId,
        offer_id:offerId,
        decision
      }
    }
  );

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}